import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";

const fmt = new Intl.NumberFormat("en-IN", {
  maximumFractionDigits: 2,
});

const priceFmt = new Intl.NumberFormat("en-IN", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

export default function PriceComparisonOverYearsDetail() {
  const navigate = useNavigate();

  const [currency, setCurrency] = useState("USD");
  const [qtyUnit, setQtyUnit] = useState("LBS");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedVariety, setSelectedVariety] = useState("");
  const [selectedGrade, setSelectedGrade] = useState("");

  const [summaryRows, setSummaryRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [meta, setMeta] = useState({});
  const [detailRows, setDetailRows] = useState([]);
  const [detailSummary, setDetailSummary] = useState({});

  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  useEffect(() => {
    let active = true;

    async function loadSummary() {
      setLoading(true);
      setError("");

      try {
        const res = await api.post(
          "/ai/ceo-chat/price-comparison-over-years",
          {
            currency,
            qty_unit: qtyUnit,
            variety: selectedVariety || "",
            grade: selectedGrade || "",
          },
          { timeout: 120000 }
        );

        if (!active) return;

        const data = res.data || {};
        setSummaryRows(data.rows || []);
        setSummary(data.summary || {});
        setMeta(data);
      } catch (e) {
        if (!active) return;

        setError(
          e?.response?.data?.detail ||
            e?.message ||
            "Price comparison could not be loaded."
        );
      } finally {
        if (active) setLoading(false);
      }
    }

    loadSummary();

    return () => {
      active = false;
    };
  }, [currency, qtyUnit, selectedVariety, selectedGrade]);

  useEffect(() => {
    let active = true;

    async function loadDetail() {
      setDetailLoading(true);
      setError("");

      try {
        const res = await api.post(
          "/ai/ceo-chat/price-comparison-over-years/detail",
          {
            currency,
            qty_unit: qtyUnit,
            order_year: selectedYear || null,
            variety: selectedVariety || "",
            grade: selectedGrade || "",
          },
          { timeout: 120000 }
        );

        if (!active) return;

        const data = res.data || {};
        setDetailRows(data.rows || []);
        setDetailSummary(data.summary || {});
        setPage(1);
      } catch (e) {
        if (!active) return;

        setError(
          e?.response?.data?.detail ||
            e?.message ||
            "Price comparison drill-down could not be loaded."
        );
      } finally {
        if (active) setDetailLoading(false);
      }
    }

    loadDetail();

    return () => {
      active = false;
    };
  }, [currency, qtyUnit, selectedYear, selectedVariety, selectedGrade]);

  const varieties = useMemo(() => {
    return Array.from(
      new Set(
        detailRows
          .map((r) => r?.Variety)
          .filter((v) => v && v !== "Unassigned")
      )
    ).sort();
  }, [detailRows]);

  const grades = useMemo(() => {
    return Array.from(
      new Set(detailRows.map((r) => r?.Grade).filter(Boolean))
    ).sort();
  }, [detailRows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return detailRows;

    return detailRows.filter((row) =>
      Object.values(row || {}).some((value) =>
        String(value ?? "").toLowerCase().includes(q)
      )
    );
  }, [detailRows, search]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredRows.length / pageSize)
  );

  const safePage = Math.min(page, totalPages);

  const pagedRows = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, safePage, pageSize]);

  const exportCsv = () => {
    const cols = [
      ["GradeLineID", "Grade Line ID"],
      ["PoNumber", "PO Number"],
      ["PoDate", "PO Date"],
      ["OrderYear", "Year"],
      ["Currency", "Currency"],
      ["QtyUnit", "Qty Unit"],
      ["Variety", "Variety"],
      ["VarietyName", "Variety Name"],
      ["Grade", "Grade"],
      ["PackingStyle", "Packing Style"],
      ["MCS", "MCS"],
      ["UnitPrice", "Unit Price"],
      ["ComparableWeight", "Comparable Weight"],
      ["ComparableLineValue", "Comparable Line Value"],
    ];

    const esc = (v) => {
      const s = String(v ?? "");
      if (/[",\n]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    const lines = [
      cols.map(([, label]) => esc(label)).join(","),
      ...filteredRows.map((row) =>
        cols.map(([key]) => esc(row?.[key])).join(",")
      ),
    ];

    const blob = new Blob([lines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `price-comparison-${currency}-${qtyUnit}${
      selectedYear ? `-${selectedYear}` : ""
    }.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const cards = [
    {
      label: "Years",
      value: fmt.format(summary.years || 0),
      sub: "Historical comparison years",
    },
    {
      label: "Priced Grade Lines",
      value: fmt.format(summary.priced_lines || 0),
      sub: "ScrmPoGradeData lines",
    },
    {
      label: "Comparable Weight",
      value: fmt.format(summary.total_comparable_weight || 0),
      sub: `${currency} / ${qtyUnit} comparable basis`,
    },
    {
      label: "Weighted Avg Price",
      value:
        summary.overall_weighted_avg_price == null
          ? "—"
          : priceFmt.format(summary.overall_weighted_avg_price),
      sub: `${currency} per ${qtyUnit}`,
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="mx-auto max-w-[1700px] space-y-5">
        <div className="flex flex-col gap-3 rounded-2xl border bg-white p-5 shadow-sm md:flex-row md:items-start md:justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              R24 · CEO Price Comparison
            </div>

            <h1 className="mt-1 text-2xl font-semibold text-slate-900">
              Price Comparison Over Years
            </h1>

            <p className="mt-2 max-w-4xl text-sm text-slate-600">
              Historical ERP selling-price comparison using
              ScrmPoGradeData.Price. Prices are compared only within the same
              currency and quantity unit. The primary KPI is weighted average
              selling price based on ERP packing-style weight calculations.
            </p>
          </div>

          <button
            type="button"
            onClick={() =>
              navigate("/ceo-command-center?tab=comparisons")
            }
            className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Back to Comparisons
          </button>
        </div>

        {error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm text-slate-600">
              <span className="mb-1 block text-xs font-semibold uppercase">
                Currency
              </span>
              <select
                value={currency}
                onChange={(e) => {
                  setCurrency(e.target.value);
                  setSelectedYear("");
                }}
                className="rounded-xl border border-slate-300 px-3 py-2"
              >
                <option value="USD">USD</option>
                <option value="INR">INR</option>
                <option value="EURO">EURO</option>
              </select>
            </label>

            <label className="text-sm text-slate-600">
              <span className="mb-1 block text-xs font-semibold uppercase">
                Quantity Unit
              </span>
              <select
                value={qtyUnit}
                onChange={(e) => {
                  setQtyUnit(e.target.value);
                  setSelectedYear("");
                }}
                className="rounded-xl border border-slate-300 px-3 py-2"
              >
                <option value="LBS">LBS</option>
                <option value="KGS">KGS</option>
              </select>
            </label>

            <label className="text-sm text-slate-600">
              <span className="mb-1 block text-xs font-semibold uppercase">
                Year
              </span>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="rounded-xl border border-slate-300 px-3 py-2"
              >
                <option value="">All Years</option>
                {summaryRows.map((row) => (
                  <option key={row.order_year} value={row.order_year}>
                    {row.order_year}
                  </option>
                ))}
              </select>
            </label>

            <label className="text-sm text-slate-600">
              <span className="mb-1 block text-xs font-semibold uppercase">
                Variety
              </span>
              <select
                value={selectedVariety}
                onChange={(e) => setSelectedVariety(e.target.value)}
                className="min-w-[160px] rounded-xl border border-slate-300 px-3 py-2"
              >
                <option value="">All Varieties</option>
                {varieties.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </label>

            <label className="text-sm text-slate-600">
              <span className="mb-1 block text-xs font-semibold uppercase">
                Grade
              </span>
              <select
                value={selectedGrade}
                onChange={(e) => setSelectedGrade(e.target.value)}
                className="min-w-[140px] rounded-xl border border-slate-300 px-3 py-2"
              >
                <option value="">All Grades</option>
                {grades.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="mt-3 text-xs text-slate-500">
            No FX conversion is applied. KGS and LBS are compared separately.
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {cards.map((card) => (
            <div
              key={card.label}
              className="rounded-2xl border bg-white p-5 shadow-sm"
            >
              <div className="text-sm font-medium text-slate-500">
                {card.label}
              </div>
              <div className="mt-2 text-3xl font-semibold text-slate-900">
                {loading ? "…" : card.value}
              </div>
              <div className="mt-2 text-xs text-slate-500">
                {card.sub}
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                Year-wise Weighted Selling Price
              </h2>
              <p className="text-sm text-slate-500">
                {currency}/{qtyUnit} · Date basis: ScrmPoData.PoDate
              </p>
            </div>

            <div className="text-xs text-slate-500">
              Reconciliation:{" "}
              <span
                className={
                  meta?.reconciliation?.pass
                    ? "font-semibold text-emerald-700"
                    : "font-semibold text-red-700"
                }
              >
                {meta?.reconciliation?.pass ? "PASS" : "PENDING"}
              </span>
            </div>
          </div>

          <div className="mt-5 overflow-x-auto">
            <table className="w-full min-w-[980px] text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-3">Year</th>
                  <th className="px-3 py-3 text-right">Weighted Avg Price</th>
                  <th className="px-3 py-3 text-right">Min Price</th>
                  <th className="px-3 py-3 text-right">Max Price</th>
                  <th className="px-3 py-3 text-right">Comparable Weight</th>
                  <th className="px-3 py-3 text-right">POs</th>
                  <th className="px-3 py-3 text-right">Grade Lines</th>
                  <th className="px-3 py-3"></th>
                </tr>
              </thead>

              <tbody>
                {summaryRows.map((row) => {
                  const active =
                    String(selectedYear) === String(row.order_year);

                  return (
                    <tr
                      key={row.order_year}
                      className={`border-b last:border-0 ${
                        active ? "bg-slate-50" : ""
                      }`}
                    >
                      <td className="px-3 py-3 font-semibold">
                        {row.order_year}
                      </td>
                      <td className="px-3 py-3 text-right font-semibold">
                        {priceFmt.format(row.weighted_avg_price || 0)}
                      </td>
                      <td className="px-3 py-3 text-right">
                        {priceFmt.format(row.min_price || 0)}
                      </td>
                      <td className="px-3 py-3 text-right">
                        {priceFmt.format(row.max_price || 0)}
                      </td>
                      <td className="px-3 py-3 text-right">
                        {fmt.format(row.comparable_weight || 0)}
                      </td>
                      <td className="px-3 py-3 text-right">
                        {fmt.format(row.po_count || 0)}
                      </td>
                      <td className="px-3 py-3 text-right">
                        {fmt.format(row.line_count || 0)}
                      </td>
                      <td className="px-3 py-3 text-right">
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedYear(active ? "" : row.order_year)
                          }
                          className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                        >
                          {active ? "Show All" : "Drill Down"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-2xl border bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b p-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                {selectedYear
                  ? `${selectedYear} Price Drill-down`
                  : "All Price Grade-line Detail"}
              </h2>

              <p className="text-sm text-slate-500">
                {fmt.format(detailSummary.grade_lines || 0)} grade lines ·{" "}
                {fmt.format(detailSummary.pos || 0)} POs · weighted average{" "}
                {detailSummary.weighted_avg_price == null
                  ? "—"
                  : priceFmt.format(detailSummary.weighted_avg_price)}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Search PO, variety, grade, packing..."
                className="w-72 rounded-xl border border-slate-300 px-3 py-2 text-sm"
              />

              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
              >
                {[25, 50, 100, 250, 500].map((n) => (
                  <option key={n} value={n}>
                    {n} / page
                  </option>
                ))}
              </select>

              <button
                type="button"
                onClick={exportCsv}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Export CSV
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[1450px] text-sm">
              <thead>
                <tr className="border-b bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-3">PO</th>
                  <th className="px-3 py-3">PO Date</th>
                  <th className="px-3 py-3">Year</th>
                  <th className="px-3 py-3">Variety</th>
                  <th className="px-3 py-3">Grade</th>
                  <th className="px-3 py-3">Packing Style</th>
                  <th className="px-3 py-3 text-right">MCS</th>
                  <th className="px-3 py-3 text-right">Unit Price</th>
                  <th className="px-3 py-3 text-right">Comparable Weight</th>
                  <th className="px-3 py-3 text-right">Line Value</th>
                </tr>
              </thead>

              <tbody>
                {detailLoading ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-4 py-8 text-center text-slate-500"
                    >
                      Loading price detail…
                    </td>
                  </tr>
                ) : pagedRows.length ? (
                  pagedRows.map((row) => (
                    <tr
                      key={row.GradeLineID}
                      className="border-b last:border-0"
                    >
                      <td className="px-3 py-3 font-medium">
                        {row.PoNumber || "—"}
                      </td>
                      <td className="px-3 py-3">{row.PoDate || "—"}</td>
                      <td className="px-3 py-3">{row.OrderYear || "—"}</td>
                      <td className="px-3 py-3">{row.Variety || "—"}</td>
                      <td className="px-3 py-3">{row.Grade || "—"}</td>
                      <td className="px-3 py-3">
                        {row.PackingStyle || "—"}
                      </td>
                      <td className="px-3 py-3 text-right">
                        {fmt.format(row.MCS || 0)}
                      </td>
                      <td className="px-3 py-3 text-right">
                        {priceFmt.format(row.UnitPrice || 0)}
                      </td>
                      <td className="px-3 py-3 text-right">
                        {fmt.format(row.ComparableWeight || 0)}
                      </td>
                      <td className="px-3 py-3 text-right">
                        {fmt.format(row.ComparableLineValue || 0)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-4 py-8 text-center text-slate-500"
                    >
                      No price detail available.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between border-t p-4 text-sm text-slate-600">
            <div>
              Showing{" "}
              {filteredRows.length
                ? (safePage - 1) * pageSize + 1
                : 0}
              –{Math.min(safePage * pageSize, filteredRows.length)} of{" "}
              {fmt.format(filteredRows.length)}
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={safePage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded-lg border px-3 py-1.5 disabled:opacity-40"
              >
                Previous
              </button>

              <span>
                Page {safePage} / {totalPages}
              </span>

              <button
                type="button"
                disabled={safePage >= totalPages}
                onClick={() =>
                  setPage((p) => Math.min(totalPages, p + 1))
                }
                className="rounded-lg border px-3 py-1.5 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
